<script setup lang="ts">
import { useRoute, useRouter } from 'vue-router'
import type { OrderCardDTO } from '@/api/orders'
import ToastComponent from '@/components/ToastComponent.vue'
import LoadingIndicator from '@/components/LoadingIndicator.vue'
import SchedulePageShell from './components/SchedulePageShell.vue'
import ScheduleHeader from './components/ScheduleHeader.vue'
import UnscheduledOrderList from './components/UnscheduledOrderList.vue'
import InspectorSidebar from './components/InspectorSidebar.vue'
import ScheduleWeekStrip from './components/ScheduleWeekStrip.vue'
import ScheduleSlotGrid from './components/ScheduleSlotGrid.vue'
import RescheduleMetaPanel from './components/RescheduleMetaPanel.vue'
import ScheduleActionBar from './components/ScheduleActionBar.vue'
import AssignConfirmModal from './components/AssignConfirmModal.vue'
import UnassignConfirmModal from './components/UnassignConfirmModal.vue'
import OccupiedSlotTooltip from './components/OccupiedSlotTooltip.vue'
export type { CareScheduleFocusPayload } from './types'
import type { CareScheduleFocusPayload } from './types'
import { useCareScheduleOrchestration } from './composables/useCareScheduleOrchestration'

const props = withDefaults(
  defineProps<{
    embedded?: boolean
    initialFocus?: CareScheduleFocusPayload | null
    externalRefreshNonce?: number
  }>(),
  { embedded: false, initialFocus: null, externalRefreshNonce: 0 },
)

const emit = defineEmits<{
  close: [list?: OrderCardDTO[]]
  'orders-updated': [list: OrderCardDTO[]]
}>()

const route = useRoute()
const router = useRouter()

const {
  embedded,
  isRescheduleMode,
  showRescheduleMetaPanel,
  schedulePageLoading,
  inspectorSidebarLoading,
  closePage,
  headerTitle,
  unscheduledPanelHeading,
  unscheduled,
  selectedOrderId,
  selectUnscheduled,
  inspectorRowViews,
  toggleInspector,
  selectInspectorAssignmentOrder,
  showInspectorSchedule,
  inspectorScheduleTitle,
  calendarMonthLabel,
  weekDayViews,
  shiftWeek,
  shiftMonth,
  selectDate,
  slotVisualStates,
  pickSlot,
  onOccupiedSlotEnter,
  onOccupiedSlotLeave,
  originalScheduledTimeParts,
  preferredTimeSlotLabels,
  unassignEnabled,
  assignEnabled,
  openCancelScheduleModal,
  onAssignClick,
  occupiedSlotHover,
  showAssignScheduleModal,
  scheduleConfirmModalText,
  closeAssignScheduleModal,
  confirmAssignSchedule,
  showCancelScheduleModal,
  cancelScheduleModalTextSnapshot,
  cancelScheduleSubmitting,
  closeCancelScheduleModal,
  confirmCancelSchedule,
  toast,
} = useCareScheduleOrchestration(props, emit, route, router)
</script>

<template>
  <SchedulePageShell
    :embedded="embedded"
    :is-reschedule-mode="isRescheduleMode"
    @outer-click="closePage()"
  >
    <ScheduleHeader
      :header-title="headerTitle"
      :embedded="embedded"
      @close="closePage"
    />

    <div v-if="schedulePageLoading" class="schedule-body schedule-body--loading" aria-busy="true">
      <LoadingIndicator min-height="320px" />
    </div>

    <div v-else class="schedule-body">
      <UnscheduledOrderList
        :heading="unscheduledPanelHeading"
        :items="unscheduled"
        :selected-order-id="selectedOrderId"
        :embedded="embedded"
        @select="selectUnscheduled"
      />

      <InspectorSidebar
        :rows="inspectorRowViews"
        :embedded="embedded"
        :is-reschedule-mode="isRescheduleMode"
        :loading="inspectorSidebarLoading"
        @toggle-inspector="toggleInspector"
        @select-assignment="
          ({ orderId, inspectorId, assignment }) =>
            selectInspectorAssignmentOrder(orderId, inspectorId, assignment)
        "
      />

      <section class="schedule-panel schedule-panel--calendar">
        <template v-if="showInspectorSchedule">
          <ScheduleWeekStrip
            :inspector-title="inspectorScheduleTitle"
            :calendar-month-label="calendarMonthLabel"
            :week-days="weekDayViews"
            :embedded="embedded"
            @shift-week="shiftWeek"
            @shift-month="shiftMonth"
            @select-date="selectDate"
          />
          <ScheduleSlotGrid
            :slot-states="slotVisualStates"
            :embedded="embedded"
            :is-reschedule-mode="isRescheduleMode"
            @pick-slot="pickSlot"
            @occupied-enter="({ slot, event }) => onOccupiedSlotEnter(slot, event)"
            @occupied-leave="onOccupiedSlotLeave"
          />
        </template>

        <RescheduleMetaPanel
          :visible="showRescheduleMetaPanel"
          :original-time="originalScheduledTimeParts.time"
          :original-inspector="originalScheduledTimeParts.inspector"
          :preferred-time-slot-labels="preferredTimeSlotLabels"
          :embedded="embedded"
          :is-reschedule-mode="isRescheduleMode"
        />

        <ScheduleActionBar
          :show-reschedule-meta-panel="showRescheduleMetaPanel"
          :unassign-enabled="unassignEnabled"
          :assign-enabled="assignEnabled"
          :is-reschedule-mode="isRescheduleMode"
          :embedded="embedded"
          @unassign="openCancelScheduleModal"
          @assign="onAssignClick"
        />
      </section>
    </div>

    <template #teleports>
      <OccupiedSlotTooltip
        :visible="!!occupiedSlotHover"
        :address="occupiedSlotHover?.address ?? ''"
        :tip-style="occupiedSlotHover?.style ?? {}"
      />
      <AssignConfirmModal
        :visible="showAssignScheduleModal"
        :message="scheduleConfirmModalText"
        @close="closeAssignScheduleModal"
        @confirm="confirmAssignSchedule"
      />
      <UnassignConfirmModal
        :visible="showCancelScheduleModal"
        :message="cancelScheduleModalTextSnapshot"
        :submitting="cancelScheduleSubmitting"
        @close="closeCancelScheduleModal"
        @confirm="confirmCancelSchedule"
      />
      <Teleport to="body">
        <ToastComponent
          :visible="toast.visible"
          :type="toast.type"
          :message="toast.message"
        />
      </Teleport>
    </template>
  </SchedulePageShell>
</template>

<style scoped>
.schedule-body--loading {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  min-height: 638px;
  background: #ffffff;
  box-sizing: border-box;
}

.schedule-body--loading :deep(.hm-loading) {
  padding: 0;
}
</style>
